import axiosInstance from "./axiosInstance.js";
import * as Sentry from "@sentry/react";
import {
  PHOTO_UPLOAD_CONCURRENCY,
  createPhotoJobs,
  runPhotoUploadWorkers,
  summarizePhotoJobs,
} from "./photoUploadQueue.js";

export function buildCloudinaryUploadForm(signed, file) {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("api_key", signed.apiKey);
  formData.append("timestamp", String(signed.timestamp));
  formData.append("signature", signed.signature);
  formData.append("folder", signed.folder);
  formData.append("public_id", signed.publicId);
  formData.append("overwrite", "false");
  formData.append("use_filename", "false");
  formData.append("unique_filename", "false");
  formData.append("allowed_formats", signed.allowedFormats);
  return formData;
}

export async function uploadFileToCloudinary(signed, file, fetchImpl = fetch) {
  const formData = buildCloudinaryUploadForm(signed, file);
  const response = await fetchImpl(
    `https://api.cloudinary.com/v1_1/${signed.cloudName}/image/upload`,
    { method: "POST", body: formData }
  );
  if (!response.ok) {
    const detail = await response.text().catch(() => "");
    throw new Error(`Cloudinary upload failed (${response.status}) ${detail}`.trim());
  }
  return response.json();
}

export async function uploadOneReportPhoto(reportId, photo, deps = {}) {
  const api = deps.api || axiosInstance;
  const uploadFile = deps.uploadFile || uploadFileToCloudinary;

  const { data: signed } = await api.post(`/reports/${reportId}/photo-uploads/sign`);
  if (signed?.maxFileSize && photo.size > signed.maxFileSize) {
    throw new Error("Photo exceeds maximum size");
  }

  const uploaded = await uploadFile(signed, photo);
  const publicId = uploaded?.public_id;
  if (!publicId) {
    throw new Error("Cloudinary did not return public_id");
  }

  await api.post(`/reports/${reportId}/photos`, { publicId });
  return publicId;
}

/**
 * Upload time-report photos after the report itself has been saved.
 * A few photos go in parallel so a flaky mobile radio is not saturated.
 * Bytes go to Cloudinary; our API only signs and then verifies the publicId.
 */
export async function uploadReportPhotos(reportId, photos, onProgress, deps = {}) {
  const uploadOne = deps.uploadOne || uploadOneReportPhoto;
  const captureException =
    deps.captureException || ((err, ctx) => Sentry.captureException(err, ctx));
  const emitProgress = (jobs) => {
    const summary = summarizePhotoJobs(jobs);
    onProgress?.({
      current: summary.current,
      total: summary.total,
      failed: summary.failed,
    });
  };

  let jobs = createPhotoJobs(reportId, photos);
  emitProgress(jobs);

  await runPhotoUploadWorkers({
    getJobs: () => jobs,
    setJobs: (next) => {
      jobs = next;
      emitProgress(jobs);
    },
    concurrency: deps.concurrency ?? PHOTO_UPLOAD_CONCURRENCY,
    uploadOne: (id, file) => uploadOne(id, file, deps),
    captureException: (error, job) =>
      captureException(error, {
        extra: {
          reportId: job.reportId,
          fileName: job.name,
          step: "uploadReportPhoto",
        },
      }),
  });

  const summary = summarizePhotoJobs(jobs);
  onProgress?.({
    current: summary.total,
    total: summary.total,
    failed: summary.failed,
  });
  return {
    uploaded: summary.ready,
    failed: summary.failed,
    total: summary.total,
  };
}
